import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  alpha,
  useTheme,
  Chip,
  Avatar,
  Grid,
  Tooltip,
  Zoom,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Notes as NotesIcon,
  Badge as BadgeIcon,
  Assignment as AssignmentIcon,
  Image as ImageIcon
} from '@mui/icons-material';

const ResponsibleForm = ({ open, onClose, onSubmit, initialData = null, isEditing = false }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    full_name: '',
    department: '',
    email: '',
    phone: '',
    position: '',
    notes: '',
    profile_image_url: '',
    is_active: true
  });

  useEffect(() => {
    if (initialData && open) {
      setFormData({
        full_name: initialData.full_name || '',
        department: initialData.department || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        position: initialData.position || '',
        notes: initialData.notes || '',
        profile_image_url: initialData.profile_image_url || '',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true
      });
    } else if (!open) {
      // Reset form when dialog closes
      setFormData({
        full_name: '',
        department: '',
        email: '',
        phone: '',
        position: '',
        notes: '',
        profile_image_url: '',
        is_active: true
      });
    }
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Gera as iniciais do nome para o avatar
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden'
        }
      }}
      TransitionComponent={Zoom}
      transitionDuration={400}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: theme.palette.mode === 'dark' 
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.primary.main, 0.05),
          py: 2.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            {isEditing ? 'Editar Responsável Técnico' : 'Novo Responsável Técnico'}
          </Typography>
        </Box>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose}
          aria-label="fechar"
          sx={{ 
            borderRadius: '50%',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              transform: 'rotate(90deg)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent 
          dividers
          sx={{ 
            px: 3, 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2 
          }}
        >
          <Grid container spacing={3}>
            {/* Dados Pessoais */}
            <Grid item xs={12} md={5}>
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2, 
                  mb: 2,
                  borderRadius: '12px',
                  border: `1px dashed ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.4)
                    : alpha(theme.palette.background.paper, 0.8),
                }}
              >
                {formData.profile_image_url ? (
                  <Avatar 
                    src={formData.profile_image_url}
                    alt={formData.full_name}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mb: 2,
                      border: `4px solid ${theme.palette.background.paper}`,
                      boxShadow: theme.shadows[3],
                    }}
                  />
                ) : (
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mb: 2,
                      bgcolor: formData.full_name 
                        ? theme.palette.primary.main 
                        : alpha(theme.palette.grey[500], 0.2),
                      border: `4px solid ${theme.palette.background.paper}`,
                      boxShadow: theme.shadows[3],
                      fontSize: '2.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {getInitials(formData.full_name) || <PersonIcon fontSize="large" />}
                  </Avatar>
                )}

                <Typography variant="h6" align="center" gutterBottom>
                  {formData.full_name || 'Nome do Responsável'}
                </Typography>

                {formData.position && (
                  <Chip 
                    label={formData.position} 
                    color="primary" 
                    size="small" 
                    variant="outlined" 
                    sx={{ mb: 1 }}
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleSwitchChange}
                      name="is_active"
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip 
                        label={formData.is_active ? 'Ativo' : 'Inativo'} 
                        size="small" 
                        color={formData.is_active ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                />

                <Grid item xs={12}>
                  <TextField
                    label="URL da Imagem de Perfil"
                    name="profile_image_url"
                    value={formData.profile_image_url}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    placeholder="https://exemplo.com/imagem.jpg"
                    helperText="URL para a imagem de perfil (opcional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ImageIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Box>

              <Box 
                sx={{ 
                  p: 2,
                  borderRadius: '12px',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.2)
                    : alpha(theme.palette.background.paper, 0.5),
                }}
              >
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary" 
                  sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <NotesIcon fontSize="small" />
                  Observações
                </Typography>
                <TextField
                  label="Observações adicionais"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={5}
                  fullWidth
                  variant="outlined"
                  placeholder="Informações adicionais sobre o responsável técnico..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Formulário principal */}
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography 
                  variant="subtitle1" 
                  color="primary" 
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                >
                  <BadgeIcon fontSize="small" />
                  Informações Pessoais
                </Typography>

                <TextField
                  label="Nome Completo"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoFocus
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />

                <TextField
                  label="Cargo"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WorkIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />

                <TextField
                  label="Departamento/Setor"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />

                <Typography 
                  variant="subtitle1" 
                  color="primary" 
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, mb: 1 }}
                >
                  <AssignmentIcon fontSize="small" />
                  Contato
                </Typography>

                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />

                <TextField
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  placeholder="(00) 00000-0000"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions 
          sx={{ 
            px: 3, 
            py: 2,
            bgcolor: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.primary.main, 0.03)
          }}
        >
          <Button 
            onClick={onClose}
            startIcon={<CancelIcon />}
            variant="outlined"
            color="inherit"
            sx={{ 
              borderRadius: '8px',
              px: 2
            }}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            sx={{ 
              borderRadius: '8px',
              px: 3
            }}
          >
            {isEditing ? 'Salvar Alterações' : 'Criar Responsável'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ResponsibleForm; 